import { cn } from '../ui/utils';

interface StackProps {
  children: React.ReactNode;
  /**
   * Spacing between items
   * @default 'md'
   */
  gap?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  /**
   * Alignment of items
   * @default 'stretch'
   */
  align?: 'start' | 'center' | 'end' | 'stretch';
  /**
   * Justify content
   * @default 'start'
   */
  justify?: 'start' | 'center' | 'end' | 'between' | 'around' | 'evenly';
  /**
   * Full width
   * @default false
   */
  fullWidth?: boolean;
  className?: string;
  /**
   * Test ID for testing
   * @default 'stack'
   */
  testId?: string;
}

const gapClasses = {
  xs: 'gap-1',
  sm: 'gap-2',
  md: 'gap-4',
  lg: 'gap-6',
  xl: 'gap-8',
  '2xl': 'gap-12',
};

const alignClasses = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
  stretch: 'items-stretch',
};

const justifyClasses = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
  between: 'justify-between',
  around: 'justify-around',
  evenly: 'justify-evenly',
};

/**
 * Stack - Vertical flex layout utility
 * 
 * Simplifies vertical stacking with consistent spacing.
 * 
 * @example
 * ```tsx
 * <Stack gap="lg" align="center">
 *   <h1>Title</h1>
 *   <p>Description</p>
 *   <Button>Action</Button>
 * </Stack>
 * ```
 */
export function Stack({
  children,
  gap = 'md',
  align = 'stretch',
  justify = 'start',
  fullWidth = false,
  className,
  testId = 'stack',
}: StackProps) {
  return (
    <div
      className={cn(
        'flex flex-col',
        gapClasses[gap],
        alignClasses[align],
        justifyClasses[justify],
        fullWidth && 'w-full',
        className
      )}
      data-testid={testId}
    >
      {children}
    </div>
  );
}
